import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../supabaseClient'
import { C, st } from '../theme'
import { EmptyState, Modal, PageHeader } from './UI'

const ALL_MODULES = [
  { key: 'wms',         label: '倉儲管理',      en: 'Warehouse',    icon: '📦' },
  { key: 'production',  label: '生產管理',      en: 'Production',   icon: '🏭' },
  { key: 'shipping',    label: '出貨管理',      en: 'Shipping',     icon: '🚚' },
  { key: 'crm',         label: '客戶管理',      en: 'CRM',          icon: '👥' },
  { key: 'reports',     label: '報表分析',      en: 'Reports',      icon: '📊' },
  { key: 'maintenance', label: '機械維修',      en: 'Maintenance',  icon: '🔧' },
  { key: 'fives',       label: '5S 管理',       en: '5S',           icon: '🏆' },
  { key: 'tasks',       label: '任務管理',      en: 'Tasks',        icon: '📋' },
  { key: 'purchasing',  label: '採購管理',      en: 'Purchasing',   icon: '🛒' },
  { key: 'attendance',  label: '出勤管理',      en: 'Attendance',   icon: '⏰' },
  { key: 'products',    label: '商品管理（進階）', en: 'Products',  icon: '🗂️' },
]

const SPECIAL_PERMS = [
  { key: 'delete_records',  label: '刪除紀錄',     icon: '🗑',  danger: true },
  { key: 'delete_products', label: '刪除商品',     icon: '❌',  danger: true },
  { key: 'manage_users',    label: '管理員工帳號', icon: '👑',  danger: true },
  { key: 'assign',          label: '派發權限',     icon: '📤',  danger: false },
  { key: 'approve_users',   label: '審核新帳號',   icon: '✅',  danger: false },
  { key: 'view_costs',      label: '查看成本價格', icon: '💰',  danger: false },
  { key: 'backup_export',   label: '全局備份匯出', icon: '🗄️',  danger: false },
]

// Preset permission templates
const PRESET_TEMPLATES = [
  { label: '管理者',    icon: '👑', perms: ['wms','production','shipping','crm','reports','maintenance','fives','tasks','purchasing','attendance','products','delete_records','delete_products','manage_users','approve_users','view_costs'] },
  { label: '廠長',      icon: '🏭', perms: ['wms','production','shipping','crm','reports','maintenance','fives','tasks','purchasing','attendance','products','delete_records','view_costs'] },
  { label: '主管',      icon: '💼', perms: ['wms','production','shipping','crm','reports','maintenance','fives','tasks','purchasing','attendance','products'] },
  { label: '倉儲',      icon: '📦', perms: ['wms','reports','fives','tasks','products','attendance'] },
  { label: '業務',      icon: '🤝', perms: ['shipping','crm','reports','tasks','products','attendance'] },
  { label: '生產線',    icon: '⚙️',  perms: ['wms','production','maintenance','fives','tasks','attendance'] },
  { label: '會計',      icon: '💳', perms: ['reports','wms','purchasing','attendance','view_costs'] },
  { label: '基本',      icon: '👤', perms: ['wms','fives','tasks','attendance'] },
]

const COLORS = ['#3d8ef0','#22c55e','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316','#ec4899','#14b8a6','#64748b','#1e40af','#166534','#92400e','#7c2d12','#4c1d95']

const DEPARTMENTS = ['管理部','生產部','倉儲部','業務部','財務部','維修部','品管部','採購部','其他']

const emptyForm = (sortOrder = 0) => ({
  name: '', name_en: '', department: '', color: '#3d8ef0', is_admin: false, permissions: {}, sort_order: sortOrder,
})

// ── Permission grid inside form ──
function PermGrid({ form, setForm, availableModules }) {
  const togglePerm = key => setForm(f => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }))
  const setAll = (keys, val) => setForm(f => {
    const p = { ...f.permissions }
    keys.forEach(k => { p[k] = val })
    return { ...f, permissions: p }
  })
  const allModulesOn  = availableModules.every(m => form.permissions[m.key])
  const allSpecialOn  = SPECIAL_PERMS.every(p => form.permissions[p.key])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* Preset templates */}
      <div>
        <label style={{ ...st.label, marginBottom: 10 }}>快速套用預設 · Quick Presets</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {PRESET_TEMPLATES.map(t => (
            <button key={t.label} type="button"
              style={{ padding: '6px 14px', borderRadius: 20, border: `1.5px solid ${C.border}`, background: '#f8fafc', fontSize: 12, fontWeight: 600, color: C.text, cursor: 'pointer' }}
              onClick={() => setForm(f => {
                const p = {}
                const allowed = new Set([...availableModules.map(m => m.key), ...SPECIAL_PERMS.map(s => s.key)])
                t.perms.filter(k => allowed.has(k)).forEach(k => { p[k] = true })
                return { ...f, permissions: p }
              })}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Module permissions */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <label style={st.label}>功能模組權限</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.accent}`, background: C.accentDim, color: C.accent, cursor: 'pointer', fontWeight: 700 }}
              onClick={() => setAll(availableModules.map(m => m.key), !allModulesOn)}>
              {allModulesOn ? '☐ 全不選' : '☑ 全選'}
            </button>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: 7 }}>
          {availableModules.map(m => {
            const on = !!form.permissions[m.key]
            return (
              <label key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 13px', borderRadius: 10, cursor: 'pointer', background: on ? C.accentDim : '#f8fafc', border: `1.5px solid ${on ? C.accent : C.border}`, transition: 'all 0.13s', minHeight: 46 }}>
                <input type="checkbox" checked={on} onChange={() => togglePerm(m.key)} style={{ width: 16, height: 16, flexShrink: 0, accentColor: C.accent }} />
                <span style={{ fontSize: 15 }}>{m.icon}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: on ? 700 : 400, color: on ? C.accent : C.text, lineHeight: 1.2 }}>{m.label}</div>
                  <div style={{ fontSize: 10, color: C.muted }}>{m.en}</div>
                </div>
              </label>
            )
          })}
        </div>
      </div>

      {/* Special permissions */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <label style={st.label}>特殊權限 <span style={{ fontSize: 11, color: C.red, fontWeight: 600 }}>（高危，請謹慎設定）</span></label>
          <button type="button" style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: `1px solid #f97316`, background: '#fff7ed', color: '#f97316', cursor: 'pointer', fontWeight: 700 }}
            onClick={() => setAll(SPECIAL_PERMS.map(p => p.key), !allSpecialOn)}>
            {allSpecialOn ? '☐ 全不選' : '☑ 全選'}
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: 7 }}>
          {SPECIAL_PERMS.map(p => {
            const on = !!form.permissions[p.key]
            return (
              <label key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 13px', borderRadius: 10, cursor: 'pointer', background: on ? '#fff7ed' : '#f8fafc', border: `1.5px solid ${on ? '#f97316' : C.border}`, transition: 'all 0.13s', minHeight: 46 }}>
                <input type="checkbox" checked={on} onChange={() => togglePerm(p.key)} style={{ width: 16, height: 16, flexShrink: 0 }} />
                <span style={{ fontSize: 15 }}>{p.icon}</span>
                <div style={{ fontSize: 12, fontWeight: on ? 700 : 400, color: on ? '#f97316' : C.text }}>{p.label}</div>
              </label>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default function PositionManager({ userProfile, showToast, onRefresh, companyModules = [] }) {
  // 只顯示超管已授權給本公司的模組（空陣列時退回全部，避免超管自用介面空白）
  const availableModules = companyModules.length > 0
    ? ALL_MODULES.filter(m => companyModules.includes(m.key))
    : ALL_MODULES
  const [positions, setPositions] = useState([])
  const [userCounts, setUserCounts] = useState({})  // { positionId: count }
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState(emptyForm())
  const [search, setSearch] = useState('')

  const load = async () => {
    const [{ data: pos }, { data: emps }] = await Promise.all([
      supabase.from('company_positions').select('*').order('sort_order').order('created_at'),
      supabase.from('user_profiles').select('position_id').not('position_id', 'is', null),
    ])
    if (pos) setPositions(pos)
    if (emps) {
      const counts = {}
      emps.forEach(e => { if (e.position_id) counts[e.position_id] = (counts[e.position_id] || 0) + 1 })
      setUserCounts(counts)
    }
  }

  useEffect(() => { load() }, [])

  const openAdd = () => {
    setEditing(null)
    setForm(emptyForm(positions.length))
    setShowModal(true)
  }

  const openEdit = (pos) => {
    setEditing(pos)
    setForm({ name: pos.name, name_en: pos.name_en || '', department: pos.department || '', color: pos.color || '#3d8ef0', is_admin: pos.is_admin || false, permissions: pos.permissions || {}, sort_order: pos.sort_order || 0 })
    setShowModal(true)
  }

  const openDuplicate = (pos) => {
    setEditing(null)
    setForm({ name: pos.name + '（副本）', name_en: pos.name_en ? pos.name_en + ' (Copy)' : '', department: pos.department || '', color: pos.color || '#3d8ef0', is_admin: false, permissions: { ...pos.permissions }, sort_order: positions.length })
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!form.name.trim()) { showToast('請填寫職位名稱', 'error'); return }
    setSaving(true)
    const payload = { ...form, name: form.name.trim() }
    if (editing) {
      const { error } = await supabase.from('company_positions').update(payload).eq('id', editing.id)
      if (error) { showToast('更新失敗：' + error.message, 'error'); setSaving(false); return }
      showToast('✓ 職位已更新')
    } else {
      const { error } = await supabase.from('company_positions').insert(payload)
      if (error) { showToast('新增失敗：' + error.message, 'error'); setSaving(false); return }
      showToast('✓ 職位已新增')
    }
    setSaving(false)
    setShowModal(false)
    setEditing(null)
    load(); onRefresh?.()
  }

  const handleDelete = async (id, name) => {
    const count = userCounts[id] || 0
    const msg = count > 0
      ? `「${name}」目前有 ${count} 位員工在使用，刪除後這些員工將失去職位設定。確定繼續？`
      : `確定刪除職位「${name}」？`
    if (!window.confirm(msg)) return
    const { data: deleted, error } = await supabase.from('company_positions').delete().eq('id', id).select('id')
    if (error) { showToast('刪除失敗：' + error.message, 'error'); return }
    if (!deleted || deleted.length === 0) {
      showToast('刪除失敗：權限不足，請確認資料庫 RLS 設定', 'error'); return
    }
    showToast('✓ 已刪除'); load(); onRefresh?.()
  }

  const handleMove = async (id, direction) => {
    const idx = positions.findIndex(p => p.id === id)
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === positions.length - 1)) return
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    const a = positions[idx], b = positions[swapIdx]
    await Promise.all([
      supabase.from('company_positions').update({ sort_order: b.sort_order }).eq('id', a.id),
      supabase.from('company_positions').update({ sort_order: a.sort_order }).eq('id', b.id),
    ])
    load()
  }

  const filtered = useMemo(() =>
    positions.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.name_en || '').toLowerCase().includes(search.toLowerCase())),
    [positions, search]
  )

  // Stats
  const adminCount = positions.filter(p => p.is_admin).length
  const totalAssigned = Object.values(userCounts).reduce((a, b) => a + b, 0)

  return (
    <div>
      <PageHeader title="👔 職位管理" en="Position Manager" subtitle="自訂公司職位與功能模組權限" />

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { icon: '👔', label: '職位總數',   value: positions.length,  color: C.accent,  bg: C.accentDim },
          { icon: '⚙️',  label: '管理員職位', value: adminCount,         color: '#7c3aed', bg: '#ede9fe' },
          { icon: '👤', label: '已指派員工', value: totalAssigned,      color: C.green,   bg: C.greenBg },
        ].map((s, i) => (
          <div key={i} style={{ background: s.bg, border: `2px solid ${s.color}33`, borderRadius: 14, padding: '14px 16px' }}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontSize: 26, fontWeight: 900, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: C.sub, fontWeight: 600, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <input style={{ ...st.input, flex: 1, minWidth: 200, maxWidth: 300 }} value={search} placeholder="🔍 搜尋職位名稱..." onChange={e => setSearch(e.target.value)} />
        <button style={{ ...st.btn(), padding: '10px 20px' }} onClick={openAdd}>
          <span>+ 新增職位</span>
          <span style={{ fontSize: 9, opacity: 0.5, display: 'block' }}>Add Position</span>
        </button>
      </div>

      {/* Empty state */}
      {positions.length === 0 && (
        <div style={{ ...st.card, textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 48, marginBottom: 14 }}>👔</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 8 }}>尚未建立任何職位</div>
          <div style={{ fontSize: 14, color: C.muted, marginBottom: 20 }}>請點「新增職位」建立公司的職位架構，並設定各職位可存取的功能模組</div>
          <button style={{ ...st.btn(), padding: '12px 28px' }} onClick={openAdd}>+ 立即新增第一個職位</button>
        </div>
      )}

      {/* Position cards */}
      {filtered.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
          {filtered.map((p, idx) => {
            const moduleCount   = availableModules.filter(m => p.permissions?.[m.key]).length
            const specialCount  = SPECIAL_PERMS.filter(s => p.permissions?.[s.key]).length
            const empCount      = userCounts[p.id] || 0
            const enabledModules = availableModules.filter(m => p.permissions?.[m.key])
            const enabledSpecial = SPECIAL_PERMS.filter(s => p.permissions?.[s.key])
            return (
              <div key={p.id} style={{ background: '#fff', borderRadius: 16, border: `2px solid ${p.color}44`, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column' }}>

                {/* Card header band */}
                <div style={{ background: `linear-gradient(135deg, ${p.color}22, ${p.color}08)`, borderBottom: `2px solid ${p.color}33`, padding: '14px 16px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                      <div style={{ width: 38, height: 38, borderRadius: 10, background: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#fff', fontWeight: 800, flexShrink: 0 }}>
                        {p.name[0]}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
                          {p.name_en && <span>{p.name_en}</span>}
                          {p.name_en && p.department && <span> · </span>}
                          {p.department && <span>🏢 {p.department}</span>}
                        </div>
                      </div>
                    </div>
                    {/* Sort arrows */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginLeft: 8 }}>
                      <button style={{ padding: '2px 6px', fontSize: 11, borderRadius: 5, border: `1px solid ${C.border}`, background: '#fff', cursor: 'pointer', color: C.muted, lineHeight: 1 }} onClick={() => handleMove(p.id, 'up')} title="上移">▲</button>
                      <button style={{ padding: '2px 6px', fontSize: 11, borderRadius: 5, border: `1px solid ${C.border}`, background: '#fff', cursor: 'pointer', color: C.muted, lineHeight: 1 }} onClick={() => handleMove(p.id, 'down')} title="下移">▼</button>
                    </div>
                  </div>

                  {/* Badges row */}
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                    {p.is_admin && (
                      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 10, background: C.accentDim, color: C.accent, fontWeight: 700 }}>⚙️ 管理員</span>
                    )}
                    <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 10, background: `${p.color}15`, color: p.color, fontWeight: 700, border: `1px solid ${p.color}33` }}>📦 {moduleCount} 個模組</span>
                    {specialCount > 0 && (
                      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 10, background: '#fff7ed', color: '#f97316', fontWeight: 700 }}>🔑 {specialCount} 個特殊</span>
                    )}
                    {empCount > 0 && (
                      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 10, background: C.greenBg, color: C.green, fontWeight: 700 }}>👤 {empCount} 人</span>
                    )}
                  </div>
                </div>

                {/* Modules preview */}
                <div style={{ padding: '12px 16px', flex: 1 }}>
                  {enabledModules.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {enabledModules.map(m => (
                        <span key={m.key} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 8, background: '#f1f5f9', color: C.sub, border: `1px solid ${C.border}` }}>{m.icon} {m.label}</span>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic' }}>尚未設定任何模組權限</div>
                  )}
                  {enabledSpecial.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 7, paddingTop: 7, borderTop: `1px dashed ${C.border}` }}>
                      {enabledSpecial.map(s => (
                        <span key={s.key} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 8, background: '#fff7ed', color: '#f97316', border: `1px solid #fed7aa` }}>{s.icon} {s.label}</span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ padding: '10px 14px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 7 }}>
                  <button style={{ ...st.btn('warning'), flex: 1, padding: '8px 10px', fontSize: 13 }} onClick={() => openEdit(p)}>✏️ 編輯</button>
                  <button style={{ ...st.btn('default'), padding: '8px 12px', fontSize: 13 }} onClick={() => openDuplicate(p)} title="複製此職位">⧉</button>
                  <button style={{ ...st.btn('danger'), padding: '8px 12px', fontSize: 13 }} onClick={() => handleDelete(p.id, p.name)}>🗑</button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {search && filtered.length === 0 && positions.length > 0 && (
        <EmptyState icon="🔍" text={`找不到符合「${search}」的職位`} />
      )}

      {/* Add / Edit Modal */}
      {showModal && (
        <Modal title={editing ? `✏️ 編輯職位 — ${editing.name}` : '+ 新增職位'} onClose={() => { setShowModal(false); setEditing(null) }}>

          {/* Basic info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
            <div>
              <label style={st.label}>職位名稱 * （中文）</label>
              <input style={st.input} value={form.name} placeholder="例：廠長、業務員" onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus />
            </div>
            <div>
              <label style={st.label}>職位名稱（英文）</label>
              <input style={st.input} value={form.name_en} placeholder="e.g. Factory Manager" onChange={e => setForm(f => ({ ...f, name_en: e.target.value }))} />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={st.label}>所屬部門</label>
              <select style={st.input} value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))}>
                <option value="">-- 不指定部門 --</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          {/* Color */}
          <div style={{ marginBottom: 16 }}>
            <label style={st.label}>職位顏色</label>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
              {COLORS.map(color => (
                <button key={color} type="button" onClick={() => setForm(f => ({ ...f, color }))}
                  style={{ width: 32, height: 32, borderRadius: 8, background: color, border: `3px solid ${form.color === color ? '#1e3a5f' : 'transparent'}`, cursor: 'pointer', boxShadow: form.color === color ? `0 0 0 2px ${color}55` : 'none', flexShrink: 0 }} />
              ))}
              <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                style={{ width: 32, height: 32, borderRadius: 8, border: `2px solid ${C.border}`, cursor: 'pointer', padding: 2 }} />
              <span style={{ fontSize: 12, color: C.muted, fontFamily: 'monospace' }}>{form.color}</span>
            </div>
          </div>

          {/* Admin toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 10, cursor: 'pointer', background: form.is_admin ? C.accentDim : '#f8fafc', border: `2px solid ${form.is_admin ? C.accent : C.border}`, marginBottom: 20 }}>
            <input type="checkbox" checked={form.is_admin} onChange={e => setForm(f => ({ ...f, is_admin: e.target.checked }))} style={{ width: 18, height: 18 }} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: form.is_admin ? C.accent : C.text }}>⚙️ 管理員職位</div>
              <div style={{ fontSize: 12, color: C.muted }}>此職位可管理員工帳號和系統設定</div>
            </div>
          </label>

          {/* Permission grid */}
          <PermGrid form={form} setForm={setForm} availableModules={availableModules} />

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
            <button style={{ ...st.btn(), flex: 1, padding: '14px' }} onClick={handleSave} disabled={saving}>
              {saving ? '儲存中...' : editing ? '確認更新' : '確認新增'}
            </button>
            <button style={{ ...st.btn('default'), padding: '14px 20px' }} onClick={() => { setShowModal(false); setEditing(null) }}>取消</button>
          </div>
        </Modal>
      )}
    </div>
  )
}
